/**
 * Client line identity for cart / billing / checkout wire contracts.
 *
 * Backend {@code CartItemDto.client_item_id} is {@code Guid} (SQL UNIQUEIDENTIFIER).
 * Non-GUID values (e.g. {@code cid-...}) fail System.Text.Json body binding → null request → 400.
 *
 * {@code crypto.randomUUID()} is secure-context-only. CUSTOM_HOSTNAME QA on
 * {@code http://order.qa-restaurant.local} is not a secure context, so we polyfill.
 */

const RFC4122_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isRfc4122Uuid(value: string | null | undefined): boolean {
  const id = String(value ?? "").trim();
  return RFC4122_UUID_RE.test(id);
}

function bytesToUuidV4(bytes: Uint8Array): string {
  // RFC 4122 version 4 / variant 1
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function uuidV4Fallback(): string {
  const bytes = new Uint8Array(16);
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytesToUuidV4(bytes);
}

/** Always returns an RFC 4122 UUID suitable for backend Guid binding. */
export function newClientItemId(): string {
  try {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
      return cryptoObj.randomUUID();
    }
  } catch {
    // Non-secure contexts / missing Web Crypto — fall through to polyfill.
  }
  return uuidV4Fallback();
}

/** Remint non-UUID ids so persisted {@code cid-...} carts remain API-compatible. */
export function ensureClientItemId(value: string | null | undefined): string {
  const id = String(value ?? "").trim();
  return isRfc4122Uuid(id) ? id : newClientItemId();
}
